import { Typography } from 'antd';
import {
  ScopeSectionCreateRequest,
  ScopeSectionResponse,
  ScopeSectionRuleRequest,
  ScopeSectionRuleTypeName,
  ScopeResponse,
} from '@ydistri/api-sdk';
import { ActionsBar, Column, IconButton, Section } from '@ydistri/ds';
import React, { useMemo } from 'react';
import type { ScopeSectionRuleOptions } from '../../../apis/apiScopes';
import { getRequestErrorMessage } from '../../../lib/utils/getRequestErrorMessage';
import ScopeSections from './ScopeSections';
import ScopeSharingIcon from './ScopeSharingIcon';
import type { ScopeRuleDefinition } from './scopeEditorModel';
import { getScopeEditorWarnings } from './scopeEditorWarnings';
import { toScopeCategoryOptions } from './scopeCategoryOptions';
import { toScopeEntityListOptions } from './scopeEntityListOptions';
import {
  ScopeActiveEntityCollections,
  ScopeRuleMultiselectEntity,
  toScopeActiveEntityOptions,
} from './scopeActiveEntityOptions';
import useScopeEditorPersistence from './useScopeEditorPersistence';
import ToggleArchivedAdministrationItemButton from '../../../screens/ProjectAdministration/common/buttons/ToggleArchivedAdministrationItemButton';
import EditIconButton from '../../buttons/EditIconButton';

export interface ScopeEditorProps {
  activeEntities?: ScopeActiveEntityCollections;
  detailsContent?: React.ReactNode;
  embedded?: boolean;
  footerContent?: React.ReactNode;
  ruleOptions?: ScopeSectionRuleOptions;
  isLoadingOptions: boolean;
  isSaving: boolean;
  onArchiveStateChange?: (isArchived: boolean) => Promise<void>;
  onEditAttributes?: () => void;
  onSharedStateChange?: (isShared: boolean) => Promise<void>;
  onCreateSection: (request: ScopeSectionCreateRequest) => Promise<ScopeSectionResponse>;
  onDeleteRule: (
    scopeSectionId: number,
    scopeSectionRuleTypeId: ScopeSectionRuleTypeName,
  ) => Promise<void>;
  onDeleteSection: (scopeSectionId: number) => Promise<void>;
  onUpsertRule: (scopeSectionId: number, request: ScopeSectionRuleRequest) => Promise<void>;
  readOnly?: boolean;
  selection: ScopeResponse;
}

const ruleDefinitions: ScopeRuleDefinition[] = [
  { value: ScopeSectionRuleTypeName.Category, label: 'Category' },
  { value: ScopeSectionRuleTypeName.Region, label: 'Region' },
  { value: ScopeSectionRuleTypeName.StoreType, label: 'Store type' },
  { value: ScopeSectionRuleTypeName.Store, label: 'Store' },
  { value: ScopeSectionRuleTypeName.Department, label: 'Department' },
  { value: ScopeSectionRuleTypeName.SkuClass, label: 'SKU class' },
  { value: ScopeSectionRuleTypeName.Brand, label: 'Brand' },
  { value: ScopeSectionRuleTypeName.EntityList, label: 'Custom list' },
  { value: ScopeSectionRuleTypeName.Product, label: 'Product' },
];

const getRuleMultiselectData = (
  type: ScopeSectionRuleTypeName,
  options?: ScopeSectionRuleOptions,
  activeEntities?: ScopeActiveEntityCollections,
  selectedValueIds: number[] = [],
): ScopeRuleMultiselectEntity[] => {
  switch (type) {
    case ScopeSectionRuleTypeName.Category:
      return options ? toScopeCategoryOptions(options.categories) : [];
    case ScopeSectionRuleTypeName.Region:
      return toScopeActiveEntityOptions(
        activeEntities?.regions ?? [],
        options?.regions ?? [],
        selectedValueIds,
      );
    case ScopeSectionRuleTypeName.StoreType:
      return toScopeActiveEntityOptions(
        activeEntities?.storeTypes ?? [],
        options?.storeTypes ?? [],
        selectedValueIds,
      );
    case ScopeSectionRuleTypeName.Store:
      return toScopeActiveEntityOptions(
        activeEntities?.stores ?? [],
        options?.stores ?? [],
        selectedValueIds,
      );
    case ScopeSectionRuleTypeName.Department:
      return toScopeActiveEntityOptions(
        activeEntities?.departments ?? [],
        options?.departments ?? [],
        selectedValueIds,
      );
    case ScopeSectionRuleTypeName.SkuClass:
      return toScopeActiveEntityOptions(
        activeEntities?.skuClasses ?? [],
        options?.skuClasses ?? [],
        selectedValueIds,
      );
    case ScopeSectionRuleTypeName.Brand:
      return toScopeActiveEntityOptions(
        activeEntities?.brands ?? [],
        options?.brands ?? [],
        selectedValueIds,
      );
    case ScopeSectionRuleTypeName.EntityList:
      return options ? toScopeEntityListOptions(options.entityLists, selectedValueIds) : [];
    // Products are deliberately not preloaded: even one tenant can contain millions.
    // Product options arrive only through pasted lookup or persisted-ID hydration.
    case ScopeSectionRuleTypeName.Product:
      return [];
  }
};

const ScopeEditor: React.FC<ScopeEditorProps> = ({
  activeEntities,
  detailsContent,
  embedded = false,
  footerContent,
  ruleOptions,
  isLoadingOptions,
  isSaving,
  onArchiveStateChange,
  onEditAttributes,
  onSharedStateChange,
  onCreateSection,
  onDeleteRule,
  onDeleteSection,
  onUpsertRule,
  readOnly = false,
  selection,
}) => {
  const { draft, isAutoSaving, submitError, setSubmitError, updateSections } =
    useScopeEditorPersistence({
      selection,
      onCreateSection,
      onDeleteRule,
      onDeleteSection,
      onUpsertRule,
    });
  const { ruleMessagesById, sectionIdsWithWarnings, summaryMessages } = useMemo(
    () => getScopeEditorWarnings(draft, ruleOptions?.entityLists),
    [draft, ruleOptions?.entityLists],
  );
  const actionsDisabled = isSaving || isAutoSaving;
  const sharingActionLabel = selection.isShared ? 'Make Scope local' : 'Make Scope shared';
  const handleStateChange = (stateChange: Promise<void>) => {
    void stateChange.catch((error: unknown) =>
      setSubmitError(getRequestErrorMessage(error, 'The Scope could not be saved.')),
    );
  };

  const headerActions =
    onArchiveStateChange || onSharedStateChange || (!selection.isArchived && onEditAttributes) ? (
      <ActionsBar>
        {!selection.isArchived && onEditAttributes && (
          <EditIconButton
            data-testid="EditScopeAttributes"
            disabled={actionsDisabled}
            size="small"
            tooltip="Edit Scope Attributes"
            onClick={onEditAttributes}
          />
        )}
        {onSharedStateChange && (
          <IconButton
            data-testid="ToggleScopeSharing"
            disabled={actionsDisabled}
            icon={
              <ScopeSharingIcon
                color="currentColor"
                isShared={selection.isShared}
                label={sharingActionLabel}
                showTooltip={false}
              />
            }
            size="small"
            tooltip={sharingActionLabel}
            tooltipPosition="left"
            onClick={() => handleStateChange(onSharedStateChange(!selection.isShared))}
          />
        )}
        {onArchiveStateChange && (
          <ToggleArchivedAdministrationItemButton
            archived={selection.isArchived}
            disabled={actionsDisabled}
            itemName="Scope"
            onClick={() => handleStateChange(onArchiveStateChange(!selection.isArchived))}
          />
        )}
      </ActionsBar>
    ) : undefined;

  const editorContent = (
    <Column $gap="1rem">
      {detailsContent}
      {submitError && <Typography.Text type="danger">{submitError}</Typography.Text>}
      {summaryMessages.length > 0 && (
        <Typography.Text type="warning">{summaryMessages[0]}</Typography.Text>
      )}
      <ScopeSections
        footerContent={footerContent}
        isLoading={isLoadingOptions}
        readOnly={readOnly || selection.isArchived}
        onSectionsChange={updateSections}
        getRuleMultiselectData={(type, selectedValueIds) =>
          getRuleMultiselectData(type, ruleOptions, activeEntities, selectedValueIds)
        }
        ruleDefinitions={ruleDefinitions}
        ruleMessagesById={ruleMessagesById}
        sectionIdsWithWarnings={sectionIdsWithWarnings}
        sections={draft.sections}
      />
    </Column>
  );

  if (embedded) return editorContent;

  return (
    <Section header={selection.name} headerActions={headerActions} $ratio={0}>
      {editorContent}
    </Section>
  );
};

export default ScopeEditor;
